import React from 'react'
import styled, { css } from 'styled-components'
import { useKeyPress } from '@mote/helpers'
import { KeyboardKey } from '@mote/helpers/constants'
import { FocusTrap } from '../focusTrap/focusTrap'
import { Portal } from '../portal/portal'
import { ButtonGroup } from '../buttonGroup/buttonGroup'
import { CloseIcon } from '@mote/assets/icons'

export const Dialog = ({
  children,
  onClose,
  hasBackdrop = true,
  isOpen = false,
  canClose = true,
  canOutsideClickClose = true,
  canEscapeKeyClose = true,
  width,
  style,
  autoFocus = true,
  trapFocus = true,
  initialFocusRef,
  finalFocusRef,
  contentRef,
  returnFocusOnClose,
  lockFocusAcrossFrames
}) => {
  const dialogContentRef = React.useRef(null)

  useKeyPress(
    KeyboardKey.Escape,
    () => {
      if (!canEscapeKeyClose || !isOpen) return

      onClose()
    },
    {},
    [canEscapeKeyClose, isOpen]
  )

  const modalContentWrapper = React.useMemo(() => {
    const content = []

    if (hasBackdrop) {
      content.push(<Backdrop key='backdrop' style={style?.backdrop} />)
    }

    content.push(
      <Content
        key='content'
        onClick={e => {
          if (!canOutsideClickClose) return
          if (dialogContentRef.current?.contains(e.target)) return
          if (canClose && onClose) onClose()
        }}
      >
        <StyledCard ref={dialogContentRef} width={width} style={style?.card}>
          {canClose && (
            <CloseIconStyled color='#232323' onClick={_ => onClose?.()} />
          )}
          {children}
        </StyledCard>
      </Content>
    )

    return content
  }, [hasBackdrop, canOutsideClickClose, onClose, children, width, style])

  return (
    <Portal className='dialog-container'>
      <FocusTrap
        autoFocus={autoFocus}
        isDisabled={!trapFocus}
        initialFocusRef={initialFocusRef}
        finalFocusRef={finalFocusRef}
        restoreFocus={returnFocusOnClose}
        contentRef={contentRef}
        lockFocusAcrossFrames={lockFocusAcrossFrames}
      >
        <Root isOpen={isOpen}>{modalContentWrapper}</Root>
      </FocusTrap>
    </Portal>
  )
}

const Root = styled.div`
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  z-index: 9998;
  overflow: auto;
  position: fixed;
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};

  > * {
    font-family: 'DM Sans', sans-serif !important;
  }
`

const Backdrop = styled.div`
  opacity: 0.4;
  user-select: none;
  position: fixed;
  left: 0;
  bottom: 0;
  right: 0;
  top: 0;
  z-index: 9999;
  overflow: auto;
  background: rgba(0, 0, 0, 0.25);
`

const Content = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100%;
  width: 100%;
  z-index: 10000;
`

const CloseIconStyled = styled(CloseIcon)`
  cursor: pointer;
  position: absolute;
  top: 13px;
  right: 16px;
`

const StyledCard = styled.div`
  position: relative;
  background: white;
  box-shadow: 0px 12px 24px rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  margin: 16px 0;
  width: ${({ width }) => width || 480}px;
  padding: 0;

  display: grid;
  grid-template-rows: 0px auto auto 1fr auto 16px;
  grid-template-columns: 0px 1fr minmax(0, auto) 0px;
  grid-template-areas:
    '.           .            .           .'
    '.           heading      heading     .'
    '.           content      content     .'
    '.           buttonGroup  buttonGroup .'
    '.           .            .           .';
`

const DialogHeading = styled.div`
  grid-area: heading;
  font-weight: 500;
  font-size: 18px;
  line-height: 21px;
  border-bottom: 1px solid rgba(213, 212, 218, 0.5);
  padding: 0 16px;
  height: 50px;
  display: flex;
  justify-content: ${({ align }) => align || 'flex-start'};
  align-items: center;
`

const DialogContent = styled.div`
  padding: 0 16px;
  grid-area: content;
  margin-top: 16px;
`

const DialogButtonGroup = styled(ButtonGroup)`
  grid-area: buttonGroup;
  margin-top: 16px;
  padding: 0 16px;

  ${({ intent }) => {
    switch (intent) {
      case 'confirmation':
        return css`
          margin-top: 40px;
        `
      default:
      // no op
    }
  }};
`

Dialog.Heading = DialogHeading
Dialog.ButtonGroup = DialogButtonGroup
Dialog.Content = DialogContent
